/**
 * useOutputNode - Hook for managing output BlockNode creation and streaming
 *
 * Provides utilities for KnowledgeNode and ChatNode to:
 * 1. Find or create target BlockNodes for streaming content
 * 2. Stream text content to BlockNodes
 * 3. Clear BlockNode content before new streaming
 */

import { useCallback, useRef } from 'react';
import { useStore } from '@xyflow/react';
import type { Node, Edge } from '@xyflow/react';
import type * as Y from 'yjs';
import type { KnowledgeCitation } from '@deep-agents/shared-types';
import {
  createOutputNode,
  findConnectedBlockNode,
  updateBlockNodeContent,
  clearBlockNode,
} from '../utils/createOutputNode';

// ============================================================================
// TYPES
// ============================================================================

export interface UseOutputNodeOptions {
  /** ID of the source node (KnowledgeNode or ChatNode) */
  sourceNodeId: string;

  /** Yjs map containing all nodes */
  nodesMap: Y.Map<Node>;

  /** Yjs map containing all edges */
  edgesMap: Y.Map<Edge>;

  /** Yjs document for collaboration */
  ydoc: Y.Doc;

  /** Wrapper function for undo/redo support */
  makeChange: (fn: () => void) => void;
}

export interface UseOutputNodeResult {
  /**
   * Find an existing connected BlockNode or create a new one.
   * Returns the BlockNode ID.
   */
  findOrCreateTargetBlock: (title?: string) => string;

  /**
   * Check if output port is connected to a BlockNode.
   * Use this to decide whether to show inline content or stream to block.
   */
  isOutputConnected: () => boolean;

  /**
   * Get the connected BlockNode ID if it exists.
   */
  getConnectedBlockId: () => string | null;

  /**
   * Stream text to a BlockNode (append to existing content).
   * Converts plain text to HTML paragraphs.
   */
  streamToBlock: (targetId: string, text: string) => void;

  /**
   * Set the complete content of a BlockNode with optional citations.
   */
  setBlockContent: (
    targetId: string,
    content: string,
    citations?: KnowledgeCitation[]
  ) => void;

  /**
   * Clear a BlockNode's content before writing new content.
   */
  clearBlock: (targetId: string) => void;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Hook for managing output BlockNodes.
 *
 * @example
 * ```tsx
 * const { findOrCreateTargetBlock, streamToBlock, clearBlock } = useOutputNode({
 *   sourceNodeId: id,
 *   nodesMap,
 *   edgesMap,
 *   ydoc,
 *   makeChange,
 * });
 *
 * // When user clicks "Ask"
 * const handleAsk = async (question: string) => {
 *   const targetId = findOrCreateTargetBlock('Answer');
 *   clearBlock(targetId);
 *
 *   // Stream response
 *   for await (const chunk of response) {
 *     streamToBlock(targetId, chunk);
 *   }
 * };
 * ```
 */
export function useOutputNode(options: UseOutputNodeOptions): UseOutputNodeResult {
  const { sourceNodeId, nodesMap, edgesMap, makeChange, ydoc } = options;

  // Use store to get reactive access to nodes for position
  const nodes = useStore((s) => s.nodes);

  // Track accumulated content for streaming
  const accumulatedContent = useRef<Map<string, string>>(new Map());

  /**
   * Check if output port is connected to a BlockNode.
   */
  const isOutputConnected = useCallback((): boolean => {
    return findConnectedBlockNode(sourceNodeId, edgesMap, nodesMap) !== null;
  }, [sourceNodeId, edgesMap, nodesMap]);

  /**
   * Get the connected BlockNode ID.
   */
  const getConnectedBlockId = useCallback((): string | null => {
    return findConnectedBlockNode(sourceNodeId, edgesMap, nodesMap);
  }, [sourceNodeId, edgesMap, nodesMap]);

  /**
   * Find existing connected BlockNode or create a new one.
   */
  const findOrCreateTargetBlock = useCallback(
    (title?: string): string => {
      // First, check if there's already a connected BlockNode
      const existingId = findConnectedBlockNode(sourceNodeId, edgesMap, nodesMap);
      if (existingId) {
        return existingId;
      }

      // Need to create a new BlockNode
      // Get source node position
      const sourceNode = nodes.find((n: Node) => n.id === sourceNodeId);
      const sourcePosition = sourceNode?.position || { x: 0, y: 0 };

      // Create the output node
      const result = createOutputNode({
        sourceNodeId,
        sourceNodePosition: sourcePosition,
        title,
        nodesMap,
        edgesMap,
        makeChange,
      });

      return result.nodeId;
    },
    [sourceNodeId, nodesMap, edgesMap, makeChange, nodes]
  );

  /**
   * Stream text to a BlockNode by appending to accumulated content.
   */
  const streamToBlock = useCallback(
    (targetId: string, text: string): void => {
      // Get or initialize accumulated content
      const current = accumulatedContent.current.get(targetId) || '';
      const updated = current + text;
      accumulatedContent.current.set(targetId, updated);

      // Convert to HTML (simple paragraph wrapping)
      // Handle line breaks by creating separate paragraphs
      const htmlContent = updated
        .split('\n\n')
        .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
        .join('');

      // Update the BlockNode
      updateBlockNodeContent(targetId, htmlContent, undefined, nodesMap, makeChange, ydoc);
    },
    [nodesMap, makeChange, ydoc]
  );

  /**
   * Set complete content of a BlockNode.
   */
  const setBlockContent = useCallback(
    (targetId: string, content: string, citations?: KnowledgeCitation[]): void => {
      // Reset accumulated content
      accumulatedContent.current.delete(targetId);

      // Convert plain text to HTML if needed
      let htmlContent = content;
      if (!content.startsWith('<')) {
        htmlContent = content
          .split('\n\n')
          .map((para) => `<p>${para.replace(/\n/g, '<br>')}</p>`)
          .join('');
      }

      updateBlockNodeContent(targetId, htmlContent, citations, nodesMap, makeChange, ydoc);
    },
    [nodesMap, makeChange, ydoc]
  );

  /**
   * Clear a BlockNode's content.
   */
  const clearBlock = useCallback(
    (targetId: string): void => {
      // Reset accumulated content for this block
      accumulatedContent.current.delete(targetId);

      clearBlockNode(targetId, nodesMap, makeChange, ydoc);
    },
    [nodesMap, makeChange, ydoc]
  );

  return {
    findOrCreateTargetBlock,
    isOutputConnected,
    getConnectedBlockId,
    streamToBlock,
    setBlockContent,
    clearBlock,
  };
}
